import { lineItemPropStrings } from '~/lib/constants';
import { formatDate, getWeekdayName, isBlockedDate } from './calendar-utils';
import { getCartItemProperties, getCartObject } from './cart-utils';

/**
 * Sums fee costs (in dollars) for _shipping_charge property.
 * @param {Array} fees - Array of fee objects with cost in dollars
 * @returns {string} Total fee in dollars as string, or empty string if no fees
 */
export const calculateShippingCharge = (fees) => {
  if (!fees || !fees.length) return '';
  const total = fees.reduce((sum, fee) => sum + (fee.cost || 0), 0);
  return String(total);
};

export const isDeliverySurcharge = (day, feeDays) => {
  return feeDays.includes(getWeekdayName(day));
};

// [date] - is in a javascript date object format
// [feeDates] - is an array of strings - ['23-10-2024', '25-10-2024']
export const isFeeDate = (date, feeDates) => {
  const formattedDate = formatDate({ date });
  return feeDates.includes(formattedDate);
};

export const isAvailableDate = (deliveryDate) => {
  const { isBlocked, blockedConditions } = isBlockedDate(
    deliveryDate,
    globalBlockOutDates,
    productBlockOutDates,
    repeatingDisabledDates
  );

  return {
    isBlocked,
    blockedConditions,
  };
};


export const isPremiumDeliveryDay = (date, daysBeforeFreeDelivery, nextAvailableDate) => {
  if (!nextAvailableDate) return false;

  const base = new Date(nextAvailableDate);
  base.setHours(0, 0, 0, 0);

  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const MS_PER_DAY = 86_400_000; // 24 * 60 * 60 * 1000
  const diffDays = Math.floor((target - base) / MS_PER_DAY);

  return diffDays >= 0 && diffDays < daysBeforeFreeDelivery;
};

// [date] - is in a javascript date object format
// [nextAvailableDate] - is in a date in iso format string
export const checkForFee = (date) => {
  const matchingFees = [];
  const feePrecedence = window.Boarderie.feePrecedence;
  const fees = window.Boarderie.fees || [];

  const deliverySurcharge = window.Boarderie.deliverySurcharge;
  if (deliverySurcharge?.days && deliverySurcharge?.cost) {
    const daysArray = deliverySurcharge.days.split(',').map((d) => d.trim());
    if (isDeliverySurcharge(date, daysArray)) {
      matchingFees.push({
        type: 'deliverySurcharge',
        cost: deliverySurcharge.cost,
        color: deliverySurcharge.color,
      });
    }
  }

  fees.forEach((fee) => {
    if (!fee.cost || !fee.dates) return;

    const datesArray = fee.dates.split(',').map((d) => d.trim());
    const isMatch = isFeeDate(date, datesArray);

    if (isMatch) {
      matchingFees.push({
        id: fee.id,
        title: fee.title,
        cost: fee.cost,
        color: fee.color,
      });
    }
  });

  if (matchingFees.length > 1 && feePrecedence) {
    const precedentFee = matchingFees.find((fee) => fee.id === feePrecedence);
    if (precedentFee) {
      return [precedentFee];
    }
  }

  return matchingFees;
};

/**
 *
 * @param {Element} cartItem - Cart item element
 * @returns {Array} - Array of connected fee products
 */
export const getLineItemFeeProducts = (cartItem) => {
  const cartItems = cartItem.closest('cart-items') || cartItem.closest('cart-drawer-items');
  const cartObject = getCartObject(cartItems);
  const { properties } = getCartItemProperties(cartItem);

  if (properties && properties[lineItemPropStrings.feeProductAdded]) {
    const connectedFeeProduct = cartObject.items.filter(
      (item) =>
        item.properties[lineItemPropStrings.groupKey] === properties[lineItemPropStrings.groupKey] &&
        item.properties['_type'] === 'fee'
    );

    return connectedFeeProduct;
  } else {
    return [];
  }
};

/**
 *
 * @param {Element} cartItem - Cart item element
 * @returns {Array} - Array of connected add-on products
 */
export const getLineItemAddOns = (cartItem) => {
  const cartItems = cartItem.closest('cart-items') || cartItem.closest('cart-drawer-items');
  const cartObject = getCartObject(cartItems);
  const { properties } = getCartItemProperties(cartItem);

  if (properties && properties[lineItemPropStrings.addOnProductAdded]) {
    const connectedAddOnProduct = cartObject.items.filter(
      (item) =>
        item.properties[lineItemPropStrings.groupKey] === properties[lineItemPropStrings.groupKey] &&
        item.properties['_type'] === 'addon'
    );

    return connectedAddOnProduct;
  } else {
    return [];
  }
};

/**
 *
 * @param {Element} cartItem - Cart item element
 * @returns {Array} - Array of connected fee products
 */
export const getLineItemObjectFeeProducts = (cartObject, cartItemObject) => {
  const { properties } = cartItemObject;

  if (properties && properties[lineItemPropStrings.feeProductAdded]) {
    const connectedFeeProduct = cartObject.items.filter(
      (item) =>
        item.properties[lineItemPropStrings.groupKey] === properties[lineItemPropStrings.groupKey] &&
        item.properties['_type'] === 'fee'
    );

    return connectedFeeProduct;
  } else {
    return [];
  }
};

/**
 * Get connected product of an add-on product
 * @param {Element} cartItem - Cart item element(Add-on product)
 * @returns {Array} - Array of connected product
 */
export const getAddOnConnectedProduct = (cartItem) => {
  const cartItems = cartItem.closest('cart-items') || cartItem.closest('cart-drawer-items');
  const cartObject = getCartObject(cartItems);
  const { properties } = getCartItemProperties(cartItem);

  const connectedProduct = cartObject.items.filter(
    (item) =>
      item.properties[lineItemPropStrings.groupKey] === properties[lineItemPropStrings.groupKey] &&
      item.properties['_type'] !== 'addon'
  );

  return connectedProduct;
};

export const getLineItemFeeProductsByKey = (groupKey) => { };

export const getSameCorporateGifts = (items, corporateGroupKey) => {
  return items.filter(
    (item) =>
      item.properties[lineItemPropStrings.corporateGroupKey] === corporateGroupKey &&
      item.properties[lineItemPropStrings.corporateGift]
  );
};

export const isPhysicalItem = (item) => {
  return (
    !item.properties[lineItemPropStrings.egift] &&
    !item.handle.includes('protection') &&
    !item.handle.includes('gift-card') &&
    item.properties[lineItemPropStrings.type] !== 'fee' &&
    item.properties[lineItemPropStrings.type] !== 'addon'
  );
};

export const getFeeProducts = (items) => {
  return items.filter((item) => item.properties[lineItemPropStrings.type] === 'fee');
};

export const hasFeeProducts = (item) => {
  return item.properties[lineItemPropStrings.feeProductAdded];
};
